import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import MonitoringZoneMap from '../../../Map/components/MonitoringZoneMap';
import CustomBtn from '../../../../generic/CustomBtn';
import GeoJsonUploader from '../../../WizardForm/Steps/AnotherComponents/GeoJsonUploader';
import DownloadZone from '../../../DownloadZone';
import {
  ALL_REPORT_TYPE,
  SUB_REPORT_TYPE,
  AREA_PERCENTAGE_LIMIT,
} from '../../../../constans/constans';
import MapMini from '../../../Map/components/MapMini/MapMini';

const MonitoringZone = ({
  totalData,
  setTotalData,
  nextPage,
  prevPage,
  currentStage,
  setNumberSampleZones,
  projectReportType,
  setProjectReportType,
  firstDataUpload,
  // setShowRequestModal,
  fieldAgentExist,
}) => {
  const AREA_HECTARES_LIMIT = +(+totalData.square * AREA_PERCENTAGE_LIMIT).toFixed(3);

  const defaultCoordinate = {
    region: totalData.region,
    polygon: totalData.polygon,
    stagesInfo: totalData.stagesInfo || [],
    subZones: totalData.subZones || [],
    sampleZones: totalData.sampleZones || [],
    codePlus: totalData.codePlus,
    polygonCoordinate: totalData.polygonCoordinate,
    square: totalData.square,
  };
  const [coordinate, setCoordinate] = useState(defaultCoordinate);
  const [showMap, setShowMap] = useState(false);
  const [jsonFile, setJsonFile] = useState(false);
  const [usePreviousZone, setUsePreviousZone] = useState(false);
  const [useEntireZone, setUseEntireZone] = useState(false);
  const [warning, setWarning] = useState('');
  const translate = useIntl();

  const currentStageInfo = coordinate.stagesInfo.find((item) => item.stage === currentStage);
  const currentSubZone = coordinate.subZones.find((item) => item.subId === currentStageInfo?.subId);
  const currentSampleZones = coordinate.sampleZones.filter((item) => item.subId === currentSubZone?.subId);
  const calculateSampleZones = (square) => {
    const area = +square;

    if (area <= 1) return 3;
    if (area > 1 && area <= 10) return Math.ceil(0.78 * area + 2.22);
    if (area > 10 && area <= 100) return Math.ceil(0.17 * area + 8.3);
    if (area > 100 && area <= 1000) return Math.ceil(0.028 * area + 22.2);
    if (area > 1000 && area <= 10000) return Math.ceil(0.0055 * area + 44.4);
    if (area > 10000 && area <= 25000) return Math.ceil(0.0033 * area + 66.67);
    if (area > 25000) return Math.ceil(0.002 * area + 100);
  };

  const defineProjectReportType = (allNftZone) => {
    if (firstDataUpload) {
      if (allNftZone) setProjectReportType(ALL_REPORT_TYPE);
      else setProjectReportType(SUB_REPORT_TYPE);
    }
  };

  const acceptableArea = (subArea) => +subArea < AREA_HECTARES_LIMIT;

  const checkPreviousZone = (coord) => {
    const duplicates = coord.stagesInfo.filter((item) => item.subId === currentStageInfo?.subId);
    if (duplicates.length > 1) return true;
    return false;
  };

  const resetData = (coord) => {
    if (currentStageInfo && currentSubZone) {
      const resetedCoordinate = { ...coord };
      const hasPreviousZone = checkPreviousZone(coord);

      resetedCoordinate.stagesInfo.pop();

      resetedCoordinate.subZones.forEach((item) => item.polygon[0].properties.polygonColor = 'orange');

      if (hasPreviousZone) {
        return resetedCoordinate;
      }

      resetedCoordinate.subZones.pop();
      const resetedSampleZones = resetedCoordinate.sampleZones.filter((item) => item.subId !== currentSubZone.subId);
      resetedCoordinate.sampleZones = resetedSampleZones;
      return resetedCoordinate;
    }
    return coord;
  };

  const handleClickDraw = () => {
    setUsePreviousZone(false);
    setUseEntireZone(false);
    setJsonFile(false);
    setShowMap(true);
  };

  const handleClickUpload = (geoJson) => {
    geoJson[0].id = `${Date.now()}`;
    setJsonFile(geoJson);
    setUsePreviousZone(false);
    setUseEntireZone(false);
  };

  const handleClickUseEntireZone = () => {
    setJsonFile(coordinate.polygon); // Need for auto update state in Map component
    setUsePreviousZone(false);
    setUseEntireZone(true);
    setShowMap(true);
  };

  const handleClickUsePrevZone = () => {
    setUsePreviousZone(true);
    setUseEntireZone(false);
    setJsonFile(false);
    setShowMap(true);
  };

  const handleClickReset = () => {
    const resetedData = resetData(coordinate);
    setCoordinate(resetedData);
    setJsonFile(false);
    setUsePreviousZone(false);
    setUseEntireZone(false);
  };

  const handleClickNext = async () => {
    if (fieldAgentExist) {
      nextPage();
    } else if (currentStageInfo && currentSubZone) {
      if (projectReportType === ALL_REPORT_TYPE) {
        // If current stage is not the first one, simply get the total number of sample plots
        const num = (currentStage > 0 && coordinate.sampleZones.length) ? coordinate.sampleZones.length : calculateSampleZones(currentSubZone.square);
        setNumberSampleZones(num);
        setWarning('');
        nextPage();
      }
      if (projectReportType === SUB_REPORT_TYPE) {
        const isValidArea = acceptableArea(currentSubZone.square);
        if (isValidArea) {
          const num = usePreviousZone ? currentSampleZones.length : calculateSampleZones(currentSubZone.square);
          setNumberSampleZones(num);
          setWarning('');
          nextPage();
        } else setWarning(translate.formatMessage({ id: 'dataUploadWizard.monitoringZone.subWarning' })`${AREA_HECTARES_LIMIT} hectares`);
      }
    } else {
      setWarning(translate.formatMessage({ id: 'dataUploadWizard.monitoringZone.warning' }));
    }
  };

  const getHtmlView = (firstUpload, type) => {
    if (fieldAgentExist) {
      return (
        <>
          <h3 className="upload-wizard__monitoring-header">
            {translate.formatMessage({ id: 'dataUploadWizard.monitoringZone.review' })}
          </h3>
          <p className="upload-wizard__monitoring-text">
            {translate.formatMessage({ id: 'dataUploadWizard.monitoringZone.review-description' })}
          </p>
        </>
      );
    }
    if (firstUpload) {
      return (
        <>
          <h3 className="upload-wizard__monitoring-header">
            {translate.formatMessage({ id: 'dataUploadWizard.monitoringZone.define' })}
          </h3>
          <FormattedMessage
            id="dataUploadWizard.monitoringZone.define-description"
            values={{
              div: (...chunks) => <div className="upload-wizard__monitoring-text">{chunks}</div>,
              u: (chunks) => <u>{chunks}</u>,
              p: (chunks) => <p>{chunks}</p>,
            }}
          />
          <div className="upload-wizard__monitoring-action">
            <CustomBtn
              label={translate.formatMessage({ id: 'dataUploadWizard.monitoringZone.button-draw' })}
              handleClick={handleClickDraw}
              type="button"
              iconClass="icon-marker"
              customClass="upload-wizard__draw"
            />
            <GeoJsonUploader
              customClass="upload-wizard__upload"
              subZones
              setJsonFile={setJsonFile}
              setShowMap={setShowMap}
              handleClickUpload={handleClickUpload}
            />
            <CustomBtn
              label={translate.formatMessage({ id: 'dataUploadWizard.monitoringZone.button-drawAll' })}
              handleClick={handleClickUseEntireZone}
              type="button"
              customClass="upload-wizard__all-nft"
            />
            <CustomBtn
              label={translate.formatMessage({ id: 'button-reset' })}
              handleClick={handleClickReset}
              type="button"
              iconClass="icon-reload-circle"
              customClass="btn__reset upload-wizard__reset"
            />
          </div>
        </>
      );
    }
    if (type === ALL_REPORT_TYPE) {
      return (
        <>
          <h3 className="upload-wizard__monitoring-header">
            {translate.formatMessage({ id: 'dataUploadWizard.monitoringZone.review' })}
          </h3>
          <p className="upload-wizard__monitoring-text">
            {translate.formatMessage({ id: 'dataUploadWizard.monitoringZone.review-description2' })}
          </p>
        </>
      );
    }
    if (type === SUB_REPORT_TYPE) {
      return (
        <>
          <h3 className="upload-wizard__monitoring-header">
            {translate.formatMessage({ id: 'dataUploadWizard.monitoringZone.define' })}
          </h3>
          <FormattedMessage
            id="dataUploadWizard.monitoringZone.define-description2"
            values={{
              div: (...chunks) => <div className="upload-wizard__monitoring-text">{chunks}</div>,
              p: (chunks) => <p>{chunks}</p>,
            }}
          />
          <div className="upload-wizard__monitoring-action">
            <CustomBtn
              label={translate.formatMessage({ id: 'dataUploadWizard.monitoringZone.button-draw' })}
              handleClick={handleClickDraw}
              type="button"
              iconClass="icon-marker"
              customClass="upload-wizard__draw"
            />
            <GeoJsonUploader
              customClass="upload-wizard__upload"
              subZones
              setJsonFile={setJsonFile}
              setShowMap={setShowMap}
              handleClickUpload={handleClickUpload}
            />
            <CustomBtn
              label={translate.formatMessage({ id: 'dataUploadWizard.monitoringZone.button-drawPrevious' })}
              handleClick={handleClickUsePrevZone}
              type="button"
              customClass="upload-wizard__all-nft"
            />
            <CustomBtn
              label={translate.formatMessage({ id: 'button-reset' })}
              handleClick={handleClickReset}
              type="button"
              iconClass="icon-reload-circle"
              customClass="btn__reset upload-wizard__reset"
            />
          </div>
        </>
      );
    }
  };

  useEffect(() => {
    if (!firstDataUpload && projectReportType === ALL_REPORT_TYPE && !currentStageInfo) {
      const updCoordinate = { ...coordinate };
      updCoordinate.stagesInfo.push({
        stage: currentStage,
        subId: updCoordinate.polygon[0].id,
        filesCidDir: '',
        additional: '',
      });
      setCoordinate(updCoordinate);
    }
  }, []);

  useEffect(() => {
    const updCoordinate = { ...coordinate };
    updCoordinate.polygon[0].properties.polygonColor = 'green';
    setCoordinate(updCoordinate);
    if (!currentSubZone) {
      updCoordinate.subZones.forEach((item) => item.polygon[0].properties.polygonColor = 'orange');
    }
  }, []);

  useEffect(() => {
    setWarning('');
    setTotalData({ ...totalData, ...coordinate });
  }, [coordinate]);

  return (
    <div className="upload-wizard__monitoring">
      {getHtmlView(firstDataUpload, projectReportType)}
      {showMap && (
        <MonitoringZoneMap
          state={coordinate}
          setState={setCoordinate}
          setShow={setShowMap}
          currentStage={currentStage}
          uploadedFile={jsonFile}
          setUploadedFile={setJsonFile}
          defineProjectReportType={defineProjectReportType}
          acceptableArea={acceptableArea}
          hectaresLimit={AREA_HECTARES_LIMIT}
          usePreviousZone={usePreviousZone}
          useEntireZone={useEntireZone}
          resetData={resetData}
          hasPreviousZone={checkPreviousZone({ ...coordinate })}
          projectReportType={projectReportType}
        />
      )}
      {!showMap && (
      <MapMini
        state={coordinate}
      />
      )}
      {!!currentStageInfo && !!currentSubZone && (
        <>
          <div className="upload-wizard__monitoring-download">
            <DownloadZone
              customClass=""
              showPosition="bottom"
              fileName={`ofp_${totalData.name}_allMonitoring`}
              coordinate={{ ...coordinate, name: totalData.name }}
              buttonText={translate.formatMessage({ id: 'sampleData.downloadMonitoringZone' })}
              downloadAll="all"
            />
          </div>
          <div className="upload-wizard__monitoring-info">
            <div className="upload-wizard__monitoring-area">
              <span>
                {translate.formatMessage({ id: 'dataUploadWizard.monitoringZone.totalAreaMonitored' })}
              </span>
              <p className="upload-wizard__monitoring-value">
                {coordinate.subZones.reduce((sum, current) => sum + +current.square, 0)}
              </p>
            </div>
            {/*
              Show div if field agent exists or if user has chosen all nft zone to report
              in first data upload (current data upload is not first)
            */}
            {((!firstDataUpload && projectReportType === ALL_REPORT_TYPE) || fieldAgentExist) && (
              <div className="upload-wizard__monitoring-area">
                <span>
                  {translate.formatMessage({ id: 'sampleData.numberOfSamplePlots' })}
                </span>
                <p className="upload-wizard__monitoring-value">
                  {coordinate.sampleZones.length}
                </p>
              </div>
            )}
          </div>
        </>
      )}
      <div className="upload-wizard__monitoring-footer">
        {warning && <span className="upload-wizard__monitoring-warning">{warning}</span>}
        <CustomBtn
          label={translate.formatMessage({ id: 'button-back' })}
          handleClick={prevPage}
          type="button"
          customClass="btn__cancel"
        />
        <CustomBtn
          label={translate.formatMessage({ id: 'button-save' })}
          handleClick={handleClickNext}
          type="submit"
          customClass="btn__btn-next"
        />
      </div>
    </div>
  );
};

export default MonitoringZone;
